/**
 * The six interaction previews' logic — "what shape would a kid face here, unanswered".
 *
 * NO GRADING, EVER (§A-4). Nothing here evaluates, scores or marks, and nothing is imported from
 * app-student — a different repo, not a dependency; these are admin-local renderers.
 *
 * THE ONE AUTHORING EXCEPTION: which option is `correct`, and what a gap accepts, are authoring
 * information an author cannot proof-read blind. So the previews expose them, rendered inside
 * `PreviewAuthorNote`, the preview's one marker for what a kid would not see.
 *
 * `GAP_MARKER` and `DRAG_GAP_REGEX` come from `lib/assessments/schema.ts`, so the preview, the
 * editor's hints and the Zod refinements cannot drift apart. Both are split with
 * `String.prototype.split`, which ignores `lastIndex`, so a global regex is safe to reuse.
 *
 * EVERYTHING IS DEFENSIVE: `options` may be missing, `text` absent, `blanks` short or legacy,
 * `backgroundAsset` without a `url`. Each hook narrows on `type` and falls back to an empty shape.
 */
import { computed } from 'vue'

import type { ComputedRef } from 'vue'
import type { Blank, Choice, MultipleChoice } from './interfaces'
import type { Interaction, InteractionType, OpenEndedFormat } from './types'

import { DEFAULT_OPEN_ENDED_FORMATS, DRAG_GAP_REGEX, GAP_MARKER, OPEN_ENDED_FORMATS } from '../../lib/assessments/schema'

/**
 * Every interaction preview takes the same single prop, which is what makes `INTERACTION_PREVIEWS`
 * a legitimate `Record<InteractionType, Component>`. It is deliberately narrower than T11's
 * `InteractionEditorProps`: a read-only component is handed no `screenId` / `activityId`, the two
 * things it would need in order to mutate.
 */
export interface InteractionPreviewProps {
  interaction: Interaction
}

/** One piece of a gapped text: either literal text, or an empty gap a kid would fill. */
type PreviewSegment =
  | { kind: 'text', key: string, value: string }
  | { kind: 'gap', key: string, position: number, answer: string }

/** Narrows to one member of the union. `null` when the component was handed another type —
 *  impossible through the registry, but a null render beats a cast that lies. */
const narrow = <T extends InteractionType>(
  interaction: Interaction | null | undefined,
  type: T,
): Extract<Interaction, { type: T }> | null => (
  interaction?.type === type ? interaction as Extract<Interaction, { type: T }> : null
)

const usableText = (value: unknown): string => (typeof value === 'string' ? value : '')

const usableArray = <T>(value: unknown): T[] => (Array.isArray(value) ? value as T[] : [])

// --- multiple_choice -------------------------------------------------------

interface PreviewChoiceItem {
  key: string
  position: number
  text: string
  /** AUTHOR-ONLY. Rendered as an annotation, never as a kid-visible mark. */
  isCorrect: boolean
}

interface UsePreviewMultipleChoiceReturn {
  /** The radio group's `name`: without one, every `single`-mode preview on a screen would join the
   *  same anonymous group. Cosmetic today (the inputs are `disabled`), correct regardless. */
  groupName: string
  refIsMulti: ComputedRef<boolean>
  refModeLabel: ComputedRef<string>
  refChoices: ComputedRef<PreviewChoiceItem[]>
  refHasChoices: ComputedRef<boolean>
  /** AUTHOR-ONLY: no option is marked correct yet (refinement 1's case, shown not judged). */
  refHasAnswerKey: ComputedRef<boolean>
}

const MODE_LABELS: Record<MultipleChoice['mode'], string> = {
  single: 'Pick one',
  multi: 'Pick any that apply',
}

/** One per hook instance, so two previews never share a radio group. A module counter rather than
 *  Vue's `useId`, because these hooks are also exercised outside a component instance. */
let choiceGroupSeq = 0

export const usePreviewMultipleChoice = (
  interaction: () => Interaction,
): UsePreviewMultipleChoiceReturn => {
  const groupName = `preview-choice-${++choiceGroupSeq}`

  const refInteraction = computed(() => narrow(interaction(), 'multiple_choice'))

  const refIsMulti = computed<boolean>(() => refInteraction.value?.mode === 'multi')

  const refModeLabel = computed<string>(() =>
    MODE_LABELS[refIsMulti.value ? 'multi' : 'single'],
  )

  const refChoices = computed<PreviewChoiceItem[]>(() =>
    usableArray<Choice>(refInteraction.value?.options).map((choice, index) => ({
      // The index, not `choice.id`: an id is optional in practice on a half-built option, and two
      // blank ids would collide as `v-for` keys.
      key: `choice-${index}`,
      position: index + 1,
      text: usableText(choice?.text),
      isCorrect: choice?.correct === true,
    })),
  )

  const refHasChoices = computed<boolean>(() => refChoices.value.length > 0)

  const refHasAnswerKey = computed<boolean>(() => refChoices.value.some(choice => choice.isCorrect))

  return { groupName, refIsMulti, refModeLabel, refChoices, refHasChoices, refHasAnswerKey }
}

// --- fill_in_blanks --------------------------------------------------------

interface PreviewBlankItem {
  key: string
  position: number
  /** AUTHOR-ONLY. The one answer for this gap, or a statement that it has none yet. */
  answerLabel: string
}

interface UsePreviewFillInBlanksReturn {
  refSegments: ComputedRef<PreviewSegment[]>
  refGapCount: ComputedRef<number>
  refBlanks: ComputedRef<PreviewBlankItem[]>
  refHasText: ComputedRef<boolean>
}

/** The one answer for a gap. `accepted` stays an array for the player's sake and a stored document
 *  may hold several, so the first is the answer and the rest are ignored — exactly what the next
 *  save will write. `undefined` covers a gap with no blank behind it at all. */
const answerOf = (blank: Blank | undefined): string =>
  usableText(usableArray<string>(blank?.accepted)[0])

// No `refHasMismatch` / `refMismatchNote`: `blanks` is derived from the text by one codec, one
// entry per gap, so the two cannot disagree through any authoring path (refinement 2 keeps the
// tripwire). Reading past the end still falls back to "no answer yet" rather than throwing.

export const usePreviewFillInBlanks = (
  interaction: () => Interaction,
): UsePreviewFillInBlanksReturn => {
  const refInteraction = computed(() => narrow(interaction(), 'fill_in_blanks'))

  const refText = computed<string>(() => usableText(refInteraction.value?.text))

  const refHasText = computed<boolean>(() => refText.value.length > 0)

  /** The STORED text, so gaps are bare `GAP_MARKER` tokens — the author's inline `{{answer}}` form
   *  never reaches a preview. `split` on the marker: N gaps produce N+1 parts, and empty parts are
   *  dropped so two adjacent gaps do not render a stray node. Each gap carries its own answer, read
   *  positionally from `blanks` — the same pairing the player grades with. */
  const refSegments = computed<PreviewSegment[]>(() => {
    const parts = refText.value.split(GAP_MARKER)
    const blanks = usableArray<Blank>(refInteraction.value?.blanks)
    const segments: PreviewSegment[] = []
    parts.forEach((part, index) => {
      if (part) {
        segments.push({ kind: 'text', key: `text-${index}`, value: part })
      }
      if (index < parts.length - 1) {
        segments.push({
          kind: 'gap',
          key: `gap-${index}`,
          position: index + 1,
          answer: answerOf(blanks[index]),
        })
      }
    })
    return segments
  })

  const refGapCount = computed<number>(() =>
    refSegments.value.filter(segment => segment.kind === 'gap').length,
  )

  /** One entry per GAP, not per stored blank: a trailing blank with no gap to sit in answers
   *  nothing. Built off `refSegments`, so the two can never number their gaps differently. */
  const refBlanks = computed<PreviewBlankItem[]>(() =>
    refSegments.value
      .filter((segment): segment is Extract<PreviewSegment, { kind: 'gap' }> =>
        segment.kind === 'gap',
      )
      .map(segment => ({
        key: `blank-${segment.position}`,
        position: segment.position,
        answerLabel: segment.answer.trim() || 'no answer yet',
      })),
  )

  return {
    refSegments,
    refGapCount,
    refBlanks,
    refHasText,
  }
}

// --- drag_the_words --------------------------------------------------------

interface PreviewBankItem {
  key: string
  word: string
  /** AUTHOR-ONLY. A distractor belongs in no gap (the schema's own word). */
  isDistractor: boolean
}

interface UsePreviewDragTheWordsReturn {
  refSegments: ComputedRef<PreviewSegment[]>
  refGapCount: ComputedRef<number>
  refHasText: ComputedRef<boolean>
  /** The draggable bank a kid would see: gap answers + distractors (the schema's comment). */
  refBank: ComputedRef<PreviewBankItem[]>
}

export const usePreviewDragTheWords = (
  interaction: () => Interaction,
): UsePreviewDragTheWordsReturn => {
  const refInteraction = computed(() => narrow(interaction(), 'drag_the_words'))

  const refText = computed<string>(() => usableText(refInteraction.value?.text))

  const refHasText = computed<boolean>(() => refText.value.length > 0)

  /** `split` on the schema's own `DRAG_GAP_REGEX`, which carries a capture group, so the
   *  odd-indexed parts are the marked words themselves — the answer key, in gap order. */
  const refSegments = computed<PreviewSegment[]>(() => {
    const parts = refText.value.split(DRAG_GAP_REGEX)
    const segments: PreviewSegment[] = []
    let gaps = 0
    parts.forEach((part, index) => {
      if (index % 2 === 1) {
        gaps += 1
        segments.push({
          kind: 'gap',
          key: `gap-${index}`,
          position: gaps,
          answer: usableText(part),
        })
        return
      }
      if (part) {
        segments.push({ kind: 'text', key: `text-${index}`, value: part })
      }
    })
    return segments
  })

  const refGapCount = computed<number>(() =>
    refSegments.value.filter(segment => segment.kind === 'gap').length,
  )

  /**
   * Authored order, not shuffled: the player may shuffle, but a preview must be deterministic, and
   * a bank that reordered itself on every keystroke would be unreadable. The components say so.
   */
  const refBank = computed<PreviewBankItem[]>(() => {
    const answers = refSegments.value
      .filter((segment): segment is Extract<PreviewSegment, { kind: 'gap' }> =>
        segment.kind === 'gap',
      )
      .map((segment, index) => ({
        key: `answer-${index}`,
        word: segment.answer,
        isDistractor: false,
      }))
    const distractors = usableArray<string>(refInteraction.value?.distractors)
      .map((word, index) => ({
        key: `distractor-${index}`,
        word: usableText(word),
        isDistractor: true,
      }))
    return [...answers, ...distractors]
  })

  return { refSegments, refGapCount, refHasText, refBank }
}

// --- whiteboard ------------------------------------------------------------

interface UsePreviewWhiteboardReturn {
  refBackgroundUrl: ComputedRef<string>
  refHasBackground: ComputedRef<boolean>
  refBackgroundAlt: ComputedRef<string>
}

export const usePreviewWhiteboard = (
  interaction: () => Interaction,
): UsePreviewWhiteboardReturn => {
  const refInteraction = computed(() => narrow(interaction(), 'whiteboard'))

  const refBackgroundUrl = computed<string>(() => {
    const url = refInteraction.value?.backgroundAsset?.url
    return typeof url === 'string' ? url.trim() : ''
  })

  const refHasBackground = computed<boolean>(() => refBackgroundUrl.value.length > 0)

  const refBackgroundAlt = computed<string>(() =>
    usableText(refInteraction.value?.backgroundAlt),
  )

  return { refBackgroundUrl, refHasBackground, refBackgroundAlt }
}

// --- open_ended ------------------------------------------------------------

interface PreviewOpenEndedTab {
  format: OpenEndedFormat
  label: string
  isActive: boolean
}

interface UsePreviewOpenEndedReturn {
  /** Allowed formats only, always in the player's fixed order. */
  refTabs: ComputedRef<PreviewOpenEndedTab[]>
  /** One allowed format shows its input alone, with no tab strip. */
  refHasTabStrip: ComputedRef<boolean>
  /** The tab a kid lands on: the first one. The preview never switches. */
  refActiveFormat: ComputedRef<OpenEndedFormat>
}

const OPEN_ENDED_TAB_LABELS: Record<OpenEndedFormat, string> = {
  text: 'Type',
  whiteboard: 'Write by hand',
  photo: 'Upload',
}

export const usePreviewOpenEnded = (
  interaction: () => Interaction,
): UsePreviewOpenEndedReturn => {
  const refInteraction = computed(() => narrow(interaction(), 'open_ended'))

  /** Shows what the kid would get: the player treats an absent OR EMPTY list as text only, so an
   *  author mid-edit with nothing ticked still sees a textarea, while the save rule reports it. */
  const refAllowed = computed<readonly OpenEndedFormat[]>(() => {
    const formats = usableArray<OpenEndedFormat>(refInteraction.value?.formats)
    const known = OPEN_ENDED_FORMATS.filter(format => formats.includes(format))
    return known.length > 0 ? known : DEFAULT_OPEN_ENDED_FORMATS
  })

  const refActiveFormat = computed<OpenEndedFormat>(() => refAllowed.value[0] ?? 'text')

  const refTabs = computed<PreviewOpenEndedTab[]>(() =>
    refAllowed.value.map(format => ({
      format,
      label: OPEN_ENDED_TAB_LABELS[format],
      isActive: format === refActiveFormat.value,
    })),
  )

  const refHasTabStrip = computed<boolean>(() => refTabs.value.length > 1)

  return { refTabs, refHasTabStrip, refActiveFormat }
}
